import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw,
  Search,
  Shield,
  UserPlus,
  Eye,
  Trash2,
  PlayCircle,
  PauseCircle,
} from "lucide-react";

import { PERMS } from "../../../utils/perms";
import { useAccess } from "../../../utils/useAccess";
import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";

import {
  useGetAdminsQuery,
  useDeleteAdminMutation,
  useToggleAdminStatusMutation,
} from "../api/adminsApi";

function safeMsg(err: any) {
  return (
    err?.data?.message || err?.error || err?.message || "Something went wrong"
  );
}

// ✅ handles cases where dept/designation could be {id,name} or just id
function getId(x: any) {
  return String(x?.id ?? x?._id ?? x ?? "").trim();
}
function getName(x: any) {
  return String(x?.name ?? x?.title ?? x ?? "").trim();
}

function StatCard({
  title,
  value,
  hint,
}: {
  title: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <div className="text-sm text-gray-600">{title}</div>
      <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
      {hint ? <div className="text-xs text-gray-500 mt-2">{hint}</div> : null}
    </div>
  );
}

export default function AdminsPage() {
  const { user, can } = useAccess();

  // permission booleans (computed once, reusable pattern)
  const canCreate = can.perm(PERMS.admin.create);
  const canToggle = can.perm(PERMS.admin.toggle);
  const canDelete = can.perm(PERMS.admin.delete);

  // UI state
  const [q, setQ] = useState("");
  const [departmentId, setDepartmentId] = useState<string>("all");
  const [designationId, setDesignationId] = useState<string>("all");

  const {
    data = [],
    isLoading,
    error,
    refetch,
  } = useGetAdminsQuery({
    q,
    // If backend supports:
    // department_id: departmentId !== "all" ? departmentId : undefined,
    // designation_id: designationId !== "all" ? designationId : undefined,
  });

  const [delAdmin, { isLoading: deleting }] = useDeleteAdminMutation();
  const [toggleStatus, { isLoading: toggling }] =
    useToggleAdminStatusMutation();
  const busy = deleting || toggling;

  // dropdown options derived from data
  const departmentOptions = useMemo(() => {
    const map = new Map<string, string>();
    data.forEach((a: any) => {
      const id = getId(a?.department);
      const name = getName(a?.department);
      if (id && name) map.set(id, name);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const designationOptions = useMemo(() => {
    const map = new Map<string, string>();
    data.forEach((a: any) => {
      const id = getId(a?.designation);
      const name = getName(a?.designation);
      if (id && name) map.set(id, name);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  // filters
  const filteredAdmins = useMemo(() => {
    const nq = q.trim().toLowerCase();

    return data.filter((a: any) => {
      const blob = `${a?.firstname ?? ""} ${a?.lastname ?? ""} ${
        a?.staff_no ?? ""
      } ${a?.user?.email ?? ""} ${a?.department?.name ?? ""} ${
        a?.designation?.name ?? ""
      }`.toLowerCase();

      const matchesSearch = !nq || blob.includes(nq);
      const deptOk =
        departmentId === "all" || getId(a?.department) === departmentId;
      const desOk =
        designationId === "all" || getId(a?.designation) === designationId;

      return matchesSearch && deptOk && desOk;
    });
  }, [data, q, departmentId, designationId]);

  const stats = useMemo(() => {
    const total = filteredAdmins.length;
    const active = filteredAdmins.filter(
      (a: any) => String(a?.status ?? "").toLowerCase() === "active"
    ).length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [filteredAdmins]);

  const onDelete = async (id: string) => {
    if (!canDelete) return;
    if (!window.confirm("Delete this admin?")) return;
    await delAdmin(id).unwrap();
  };

  const onToggle = async (id: string) => {
    if (!canToggle) return;
    await toggleStatus(id).unwrap();
  };

  const clearFilters = () => {
    setQ("");
    setDepartmentId("all");
    setDesignationId("all");
  };

  return (
    <PageGate perm={PERMS.admin.access}>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white border rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-gray-50 border">
                <Shield className="w-5 h-5 text-gray-700" />
              </div>

              <div>
                <div className="font-semibold text-gray-900">
                  Admin Management
                </div>
                <div className="text-sm text-gray-600">
                  Signed in as{" "}
                  <span className="font-medium text-gray-900">
                    {user?.email}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm"
                onClick={() => refetch()}
                disabled={isLoading}
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>

              <AccessGate perm={PERMS.admin.create}>
                <Link
                  to="/admins/new"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm"
                >
                  <UserPlus className="w-4 h-4" />
                  Add Admin
                </Link>
              </AccessGate>
            </div>
          </div>

          {/* Search + Dropdown filters */}
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-3 lg:items-end">
            {/* Search */}
            <div className="lg:col-span-5">
              <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, email, staff no..."
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-200"
                />
              </div>
            </div>

            {/* Department */}
            <div className="lg:col-span-3">
              <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">
                Department
              </label>
              <select
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-gray-200"
              >
                <option value="all">All Departments</option>
                {departmentOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Designation */}
            <div className="lg:col-span-3">
              <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">
                Designation
              </label>
              <select
                value={designationId}
                onChange={(e) => setDesignationId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-gray-200"
              >
                <option value="all">All Designations</option>
                {designationOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear */}
            <div className="lg:col-span-1">
              <button
                type="button"
                onClick={clearFilters}
                className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Admins" value={String(stats.total)} />
          <StatCard title="Active" value={String(stats.active)} />
          <StatCard title="Inactive" value={String(stats.inactive)} />
          <StatCard
            title="My Admin Permissions"
            value={String(can.adminPerms.length)}
            hint="permissions starting with admin."
          />
        </div>

        {/* Table Card */}
        <div className="bg-white border rounded-xl p-6 shadow-sm">
          {isLoading ? (
            <div className="py-12 text-center text-gray-500">
              Loading admins...
            </div>
          ) : error ? (
            <div className="py-12 text-center text-red-600">
              {safeMsg(error)}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b text-gray-400 font-medium uppercase text-[11px]">
                      <th className="py-3 px-2 w-10">#</th>
                      <th className="py-3 px-4">Name</th>
                      <th className="py-3 px-4">Email</th>
                      <th className="py-3 px-4">Dept / Designation</th>
                      <th className="py-3 px-4">Status</th>
                      <AccessGate
                        perm={PERMS.admin.detailsAccess || PERMS.admin.toggle}
                      >
                        <th className="py-3 px-4 text-right">Actions</th>
                      </AccessGate>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredAdmins.map((a: any, index: number) => {
                      const isActive =
                        String(a?.status ?? "").toLowerCase() === "active";

                      return (
                        <tr
                          key={a?.id}
                          className="border-b hover:bg-gray-50 transition-colors"
                        >
                          <td className="py-4 px-2 text-gray-400 font-mono">
                            {(index + 1).toString().padStart(2, "0")}
                          </td>

                          <td className="py-4 px-4">
                            <div className="font-semibold text-gray-800 capitalize">
                              {a?.firstname} {a?.lastname}
                            </div>
                            <div className="text-[11px] text-gray-400 uppercase font-mono">
                              {a?.staff_no || "NO ID"}
                            </div>
                          </td>

                          <td className="py-4 px-4 text-gray-600">
                            {a?.user?.email || "—"}
                          </td>

                          <td className="py-4 px-4">
                            <div className="text-gray-700">
                              {a?.department?.name || "—"}
                            </div>
                            <div className="text-xs text-gray-400 italic">
                              {a?.designation?.name || "—"}
                            </div>
                          </td>

                          <td className="py-4 px-4">
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                isActive
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {a?.status ?? "—"}
                            </span>
                          </td>
                          <AccessGate
                            perm={
                              PERMS.admin.detailsAccess || PERMS.admin.toggle
                            }
                          >
                            <td className="py-4 px-4 text-right">
                              <div className="flex justify-end gap-2">
                                {/* VIEW always visible once page is accessible */}

                                <AccessGate perm={PERMS.admin.detailsAccess}>
                                  <Link
                                    to={`/admins/${a?.id}`}
                                    className="p-1.5 border rounded hover:bg-gray-50"
                                    title="View Detail"
                                  >
                                    <Eye className="w-4 h-4 text-gray-600" />
                                  </Link>
                                </AccessGate>
                                {/* TOGGLE */}
                                <AccessGate perm={PERMS.admin.toggle}>
                                  <button
                                    type="button"
                                    onClick={() => onToggle(a?.id)}
                                    disabled={busy}
                                    className={`p-1.5 border rounded hover:bg-gray-50 ${
                                      busy
                                        ? "opacity-60 cursor-not-allowed"
                                        : ""
                                    }`}
                                    title={isActive ? "Deactivate" : "Activate"}
                                  >
                                    {isActive ? (
                                      <PauseCircle className="w-4 h-4 text-amber-600" />
                                    ) : (
                                      <PlayCircle className="w-4 h-4 text-green-600" />
                                    )}
                                  </button>
                                </AccessGate>

                                {/* DELETE */}
                                <AccessGate perm={PERMS.admin.delete}>
                                  <button
                                    type="button"
                                    onClick={() => onDelete(a?.id)}
                                    disabled={busy}
                                    className={`p-1.5 border rounded hover:bg-red-50 text-red-600 ${
                                      busy
                                        ? "opacity-60 cursor-not-allowed"
                                        : ""
                                    }`}
                                    title="Delete Admin"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </AccessGate>
                              </div>
                            </td>
                          </AccessGate>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredAdmins.length === 0 && (
                <div className="py-16 text-center text-gray-500 text-sm">
                  No admin records found.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageGate>
  );
}
